<script lang="ts" setup>
import { computed, ref } from 'vue';
import { EmptyState, PageHero } from '~/components';
import {
  AppSkeletonLoader,
  AppTextInput,
  AppTypography,
} from '~/components/base';
import { LocationPickerModal } from '~/components/layout';
import { PartyCard, PartyFilterModal } from '~/components/parties';
import { useParties } from '~/composables/useParties';
import { useQuerySettling } from '~/composables/useQuerySettling';
import { useLocationStore } from '~/stores/location';

definePageMeta({ layout: 'default' });
useHead({ title: 'Parties' });
useSeoMeta({
  description:
    'See every party registered for the Victorian State Election and the candidates they field.',
  ogTitle: 'Parties | VoteWise',
  ogDescription:
    'See every party registered for the Victorian State Election and the candidates they field.',
});

const query = ref('');
const location = useLocationStore();
const locationModalVisible = ref(false);
const typeFilter = ref<'all' | 'major' | 'minor'>('all');
const filterModalVisible = ref(false);

const { parties, pending, error } = useParties(
  () => location.location?.suburbId ?? null,
  query,
);

const settling = useQuerySettling(query);
const isLoading = computed(() => pending.value || settling.value);

const filterLabel = computed(() => {
  if (typeFilter.value === 'major') return 'Major';
  if (typeFilter.value === 'minor') return 'Minor';
  return 'All Parties';
});

const filterLabelShort = computed(() => {
  if (typeFilter.value === 'major') return 'Major';
  if (typeFilter.value === 'minor') return 'Minor';
  return 'All';
});

// The name/abbreviation search is server-side (filter[search]) — filtering here
// would only ever have searched the 50 parties on the first page. Type is still
// local: it's a two-way toggle over an already-loaded list, so a round trip
// would add latency for nothing.
const visibleParties = computed(() =>
  parties.value.filter(
    (party) =>
      typeFilter.value === 'all' || party.classification === typeFilter.value,
  ),
);

// An empty result means two different things: nothing matched what the user
// asked for (recoverable — tell them how to broaden it), or there is simply
// nothing to show yet. "Check the spelling" is wrong advice in the latter case.
const isNarrowed = computed(
  () => query.value.trim().length > 0 || typeFilter.value !== 'all',
);
</script>

<template>
  <div class="parties-page">
    <PageHero constrained>
      <div class="parties-page__panel">
        <div
          class="parties-page__toolbar flex flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4"
        >
          <!-- Hidden on mobile: the mobile topbar already renders "Parties"
               as the page title, so a second page-level h1 would duplicate
               it (the Figma shows it once). Desktop's topbar shows the nav
               instead, so the heading belongs on the page there. -->
          <AppTypography type="title-5" element="h1" class="hidden lg:block"
            >Parties</AppTypography
          >

          <div
            class="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center"
          >
            <button
              type="button"
              data-testid="parties-filter-trigger"
              class="parties-page__filter order-2 shrink-0 self-start lg:order-1 lg:self-auto"
              @click="filterModalVisible = true"
            >
              <span class="truncate lg:hidden">{{ filterLabelShort }}</span>
              <span class="hidden truncate lg:inline">{{ filterLabel }}</span>
              <Icon name="solar:alt-arrow-down-bold" class="shrink-0" />
            </button>

            <div
              data-testid="parties-search"
              class="parties-page__search order-1 flex items-center gap-2 lg:order-2"
            >
              <Icon
                name="solar:magnifer-linear"
                class="parties-page__search-icon text-color-subtext shrink-0"
              />
              <AppTextInput
                v-model="query"
                placeholder="Search Parties"
                :floating-label="false"
                variant="outlined"
                size="small"
                enterkeyhint="search"
                class="parties-page__search-input"
              />
              <button
                type="button"
                data-testid="parties-location"
                class="parties-page__search-location flex max-w-[45%] shrink-0 items-center gap-1 rounded-full px-3 py-1.5"
                @click="locationModalVisible = true"
              >
                <Icon
                  name="solar:map-point-linear"
                  size="16"
                  class="parties-page__search-location-icon shrink-0"
                />
                <AppTypography type="caption" class="truncate">
                  {{ location.displayLabel }}
                </AppTypography>
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="isLoading"
          data-testid="parties-loading"
          class="parties-page__grid grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6"
        >
          <AppSkeletonLoader
            v-for="n in 6"
            :key="n"
            :items="[{ height: '140px' }]"
            container-class=""
          />
        </div>
        <EmptyState
          v-else-if="error"
          data-testid="parties-error-state"
          title="Couldn't load parties"
          illustration="result"
          description="Please try again."
        />
        <div
          v-else-if="visibleParties.length"
          class="parties-page__grid grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6"
        >
          <!-- First 6 cards (~2 mobile viewports) render eagerly so the
               above-the-fold LCP avatar isn't lazy-loaded. -->
          <PartyCard
            v-for="(party, index) in visibleParties"
            :key="party.id"
            :party="party"
            :eager="index < 6"
            :priority="index === 0"
          />
        </div>
        <EmptyState
          v-else-if="isNarrowed"
          data-testid="parties-empty-state"
          title="No Parties Found"
          illustration="parties"
        />
        <EmptyState
          v-else
          data-testid="parties-empty-state"
          title="No Parties Yet"
          illustration="parties"
          description="There are no parties to display at the moment."
        />
      </div>
    </PageHero>
    <LocationPickerModal
      v-if="locationModalVisible"
      v-model:visible="locationModalVisible"
    />
    <PartyFilterModal
      v-if="filterModalVisible"
      v-model:visible="filterModalVisible"
      v-model="typeFilter"
    />
  </div>
</template>

<style scoped lang="scss">
.parties-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__panel {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;

    // Figma: the whole panel sits in a big white rounded card on desktop;
    // on mobile the toolbar/cards sit directly on the sand band, each card
    // carrying its own white background.
    @media (min-width: 1024px) {
      padding: $spacing-2xl;
      border-radius: $radius-2xl;
      background-color: $surface-color-bg;
    }
  }

  &__search {
    width: 100%;
    height: 40px;
    border: 1px solid $border-color;
    border-radius: 9999px;
    padding: 0 $spacing-lg;
    background-color: $surface-color-bg;

    // Figma: rounded rectangle on mobile, matching the Candidates/Campaigns
    // search field — only the desktop pill is fully rounded.
    @media (max-width: 639.98px) {
      height: 48px;
      border-radius: $radius-md;
    }

    @media (min-width: 1024px) {
      width: 360px;
    }

    :deep(.app-floating-label-wrapper) {
      display: flex;
      align-items: center;
      width: 100%;
    }

    :deep(.p-inputtext) {
      width: 100%;
      height: 38px;
      border: none;
      background: transparent;
      box-shadow: none;
      padding: 0;

      &:focus,
      &:enabled:focus {
        border: none;
        box-shadow: none;
        outline: none;
      }
    }
  }

  &__search-icon {
    font-size: 1.125rem;
  }

  &__search-location {
    background-color: $surface-color-tag;
    border: 1px solid $surface-color-disabled;
    color: $text-color-primary;

    // Redundant with the topbar's global location pill on desktop — mobile
    // only, same as the Candidates page's equivalent trigger.
    @media (min-width: 1024px) {
      display: none;
    }
  }

  &__search-location-icon {
    color: $text-color-tag;
  }

  // Chip trigger that opens the Filters modal. Figma: mobile keeps the
  // dark-gold pill trigger, but desktop uses the light surfaces/fields-tags
  // chip (#f6f4f0 bg, #e8e3db border, #201e1a text) — same distinction as
  // the Candidates page's party-filter trigger (VTW-449/477).
  &__filter {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    max-width: 100%;
    height: 40px;
    padding: 0 $spacing-lg;
    border: none;
    border-radius: 9999px;
    background-color: #766035;
    color: $text-color-white;
    font: inherit;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;

    @media (min-width: 1024px) {
      border: 1px solid #e8e3db;
      background-color: #f6f4f0;
      color: #201e1a;
      font-weight: 400;
    }
  }
}
</style>
